/**
 * RoutesSubSection — Phase 18.15.1
 *
 * Beheert ``homepage_route_settings`` — de Populaire Routes & Bestemmingen
 * sectie op de homepage. Admins kunnen routes toevoegen, bewerken,
 * activeren/deactiveren en de volgorde aanpassen zonder code-deploy.
 *
 * Belangrijke UX-regels (gevraagd door eigenaar):
 *  - Geen harde transittijden in placeholders — alleen indicatieve copy.
 *  - Duidelijke waarschuwing dat tijden afhankelijk zijn van planning,
 *    documenten, douane en lokale afhandeling.
 *  - Alleen Owner kan definitief verwijderen — Admin/Content Manager
 *    deactiveren via de toggle.
 */
import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_ROUTE = {
  origin: "Nederland",
  destination: "",
  destination_slug: "",
  methods_label: "Zeevracht & luchtvracht mogelijk",
  transit_text: "",
  disclaimer_text: "Transittijden zijn indicatief.",
  badge: "",
  sort_order: 100,
  is_active: true,
  cta_primary_label: "Vraag offerte aan",
  cta_primary_url: "/contact?get-quote=1#quote-form",
  cta_secondary_label: "Track uw zending",
  cta_secondary_url: "/#tracking-widget",
};

export default function RoutesSubSection() {
  const { hasPermission, user } = useAuth();
  const canEdit = hasPermission("website:edit");
  const isOwner = user?.role === "owner";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {…route}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/homepage-routes");
      setRows(r.data?.routes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => {
    setEditing({ ...EMPTY_ROUTE, sort_order: (rows[rows.length - 1]?.sort_order || 0) + 10 });
  };

  const startEdit = (row) => setEditing({ ...row });

  const save = async () => {
    if (!editing.origin?.trim() || !editing.destination?.trim()) {
      toast.error("Vul origin en destination in.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await adminApi.put(`/admin/homepage-routes/${editing.id}`, editing);
        toast.success("Route bijgewerkt");
      } else {
        await adminApi.post("/admin/homepage-routes", editing);
        toast.success("Route toegevoegd");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await adminApi.put(`/admin/homepage-routes/${row.id}`, {
        is_active: !row.is_active,
      });
      toast.success(row.is_active ? "Route gedeactiveerd" : "Route geactiveerd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Definitief verwijderen: ${row.origin} → ${row.destination}?`)) return;
    try {
      await adminApi.delete(`/admin/homepage-routes/${row.id}`);
      toast.success("Route verwijderd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const move = async (row, direction) => {
    const idx = rows.findIndex((r) => r.id === row.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= rows.length) return;
    const swap = rows[swapIdx];
    try {
      await adminApi.post("/admin/homepage-routes/reorder", {
        routes: [
          { id: row.id, sort_order: swap.sort_order },
          { id: swap.id, sort_order: row.sort_order },
        ],
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Routes laden…
      </div>
    );
  }

  return (
    <div data-testid="routes-subsection">
      {/* Header + add button */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-600">
            Beheer de Populaire Routes &amp; Bestemmingen op de homepage.
            Wijzigingen zijn direct zichtbaar op{" "}
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-[#1a5193] underline hover:text-[#082c50]"
            >
              de publieke homepage
            </a>
            .
          </p>
        </div>
        {canEdit && !editing && (
          <Button
            onClick={startCreate}
            className="bg-[#082c50] hover:bg-[#1a5193] text-white rounded-none font-chivo font-bold uppercase text-xs"
            data-testid="routes-add-button"
          >
            <Plus className="w-4 h-4 mr-1" /> Nieuwe route
          </Button>
        )}
      </div>

      {/* Operationele waarschuwing */}
      <div
        className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-5 flex items-start gap-3"
        data-testid="routes-warning"
      >
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 leading-relaxed">
          <strong>Gebruik geen harde transittijden tenzij deze
          operationeel bevestigd zijn.</strong> Advies: vermeld dat tijden
          indicatief zijn en afhankelijk van planning, documenten, douane
          en lokale afhandeling. Voorbeeld: "Zeevracht gemiddeld 4–6
          weken na vertrek".
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-white border border-gray-200 rounded-md p-5 mb-5" data-testid="routes-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-chivo font-black text-lg text-[#082c50]">
              {editing.id ? "Route bewerken" : "Nieuwe route"}
            </h3>
            <button
              onClick={() => setEditing(null)}
              className="text-gray-500 hover:text-gray-800"
              aria-label="Sluiten"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Origin / vanaf-land *</Label>
              <Input
                value={editing.origin || ""}
                onChange={(e) => setEditing({ ...editing, origin: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Nederland"
                data-testid="routes-input-origin"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Destination / bestemming *</Label>
              <Input
                value={editing.destination || ""}
                onChange={(e) => setEditing({ ...editing, destination: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Curaçao"
                data-testid="routes-input-destination"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Destination slug</Label>
              <Input
                value={editing.destination_slug || ""}
                onChange={(e) => setEditing({ ...editing, destination_slug: e.target.value })}
                className="rounded-none mt-1"
                placeholder="curacao (auto)"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Methods label</Label>
              <Input
                value={editing.methods_label || ""}
                onChange={(e) => setEditing({ ...editing, methods_label: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Zeevracht & luchtvracht mogelijk"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide font-bold">Transit text (klantvriendelijk)</Label>
              <Textarea
                rows={2}
                value={editing.transit_text || ""}
                onChange={(e) => setEditing({ ...editing, transit_text: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Zeevracht gemiddeld 4–6 weken na vertrek. Luchtvracht afhankelijk van planning en documenten."
                data-testid="routes-input-transit"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Vermijd harde dagen tenzij operationeel bevestigd.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wide font-bold">Disclaimer</Label>
              <Input
                value={editing.disclaimer_text || ""}
                onChange={(e) => setEditing({ ...editing, disclaimer_text: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Transittijden zijn indicatief."
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Badge</Label>
              <Input
                value={editing.badge || ""}
                onChange={(e) => setEditing({ ...editing, badge: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Populair / Zakelijk / Op aanvraag"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Sorteervolgorde</Label>
              <Input
                type="number"
                value={editing.sort_order ?? 100}
                onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })}
                className="rounded-none mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">CTA 1 label</Label>
              <Input
                value={editing.cta_primary_label || ""}
                onChange={(e) => setEditing({ ...editing, cta_primary_label: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Vraag offerte aan"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">CTA 1 URL</Label>
              <Input
                value={editing.cta_primary_url || ""}
                onChange={(e) => setEditing({ ...editing, cta_primary_url: e.target.value })}
                className="rounded-none mt-1"
                placeholder="/contact?get-quote=1#quote-form"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">CTA 2 label</Label>
              <Input
                value={editing.cta_secondary_label || ""}
                onChange={(e) => setEditing({ ...editing, cta_secondary_label: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Track uw zending"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">CTA 2 URL</Label>
              <Input
                value={editing.cta_secondary_url || ""}
                onChange={(e) => setEditing({ ...editing, cta_secondary_url: e.target.value })}
                className="rounded-none mt-1"
                placeholder="/#tracking-widget"
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <Switch
                checked={!!editing.is_active}
                onCheckedChange={(v) => setEditing({ ...editing, is_active: v })}
                data-testid="routes-input-active"
              />
              <Label className="text-sm">
                {editing.is_active ? "Actief — zichtbaar op homepage" : "Inactief — niet zichtbaar"}
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              className="rounded-none"
            >
              Annuleren
            </Button>
            <Button
              onClick={save}
              disabled={saving || !canEdit}
              className="bg-[#082c50] hover:bg-[#1a5193] text-white rounded-none font-chivo font-bold uppercase text-xs"
              data-testid="routes-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Opslaan
            </Button>
          </div>
        </div>
      )}

      {/* Routes list */}
      {!rows.length ? (
        <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-gray-600">
          Nog geen routes ingesteld. Klik op{" "}
          <strong>Nieuwe route</strong> om er een toe te voegen.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          {rows.map((row, idx) => (
            <div
              key={row.id}
              className={`px-5 py-4 flex items-start gap-4 ${
                idx > 0 ? "border-t border-gray-100" : ""
              } ${!row.is_active ? "opacity-60" : ""}`}
              data-testid={`routes-row-${row.destination_slug || row.id}`}
            >
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => move(row, "up")}
                  disabled={idx === 0 || !canEdit}
                  className="text-gray-400 hover:text-[#082c50] disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Omhoog"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => move(row, "down")}
                  disabled={idx === rows.length - 1 || !canEdit}
                  className="text-gray-400 hover:text-[#082c50] disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Omlaag"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="font-chivo font-black text-[#082c50]">
                    {row.origin}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <span className="font-chivo font-black text-[#082c50]">
                    {row.destination}
                  </span>
                  {row.badge ? (
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#1a5193] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                      {row.badge}
                    </span>
                  ) : null}
                  {!row.is_active ? (
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                      Inactief
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {row.methods_label || "—"}
                </div>
                {row.transit_text ? (
                  <p className="text-sm text-gray-800 mt-1.5 leading-relaxed">
                    {row.transit_text}
                  </p>
                ) : null}
                {row.disclaimer_text ? (
                  <p className="text-[11px] text-gray-500 mt-1 italic">
                    {row.disclaimer_text}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                <Switch
                  checked={!!row.is_active}
                  onCheckedChange={() => toggleActive(row)}
                  disabled={!canEdit}
                  aria-label="Actief"
                />
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => startEdit(row)}
                    disabled={!canEdit}
                    className="text-xs font-bold text-[#1a5193] hover:text-[#082c50] disabled:opacity-50"
                    data-testid={`routes-edit-${row.destination_slug || row.id}`}
                  >
                    Bewerken
                  </button>
                  {isOwner ? (
                    <button
                      onClick={() => deleteRow(row)}
                      className="text-xs font-bold text-red-600 hover:text-red-800"
                      aria-label="Verwijderen"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
